/**
 * BlogModule -- Article reader for the AWENI blog.
 *
 * Fetches articles from /blog/articles-index.json, renders a searchable
 * list with category filters, and displays full articles with simple
 * markdown rendering. Supports offline cached articles.
 */

import type { AweniContext, AweniModule, BlogArticle } from '../../types';

// ---------- Simple Markdown Renderer ----------

function renderMarkdown(md: string): string {
  return md
    .replace(/^### (.+)$/gm, '<h3 class="text-lg font-bold mt-6 mb-2" style="color: var(--text-primary)">$1</h3>')
    .replace(/^## (.+)$/gm, '<h2 class="text-xl font-bold mt-8 mb-3" style="color: var(--text-primary)">$1</h2>')
    .replace(/^# (.+)$/gm, '<h1 class="text-2xl font-bold mt-8 mb-4" style="color: var(--text-primary)">$1</h1>')
    .replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" class="rounded-xl my-4 max-w-full" loading="lazy"/>')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="underline" style="color: var(--accent)" target="_blank" rel="noopener">$1</a>')
    .replace(/^---$/gm, '<hr class="my-6" style="border-color: var(--border)"/>')
    .replace(/^(?!<[h123]|<img|<ul|<ol|<li|<blockquote|<hr)(.+)$/gm, '<p class="mb-3 leading-relaxed" style="color: var(--text-secondary)">$1</p>');
}

// ---------- BlogModule Class ----------

export default class BlogModule implements AweniModule {
  private container: HTMLElement | null = null;
  private context: AweniContext | null = null;
  private articles: BlogArticle[] = [];
  private filteredArticles: BlogArticle[] = [];
  private activeCategory = '';
  private searchQuery = '';
  private viewingArticle: BlogArticle | null = null;

  async mount(container: HTMLElement, ctx: AweniContext): Promise<void> {
    this.container = container;
    this.context = ctx;

    // Load article index
    try {
      const resp = await fetch('/blog/articles-index.json', { cache: 'no-store' });
      if (resp.ok) {
        const data = await resp.json();
        this.articles = Array.isArray(data) ? data : (data.articles || []);
      }
    } catch {
      this.articles = [];
    }

    this.filteredArticles = [...this.articles];
    this.renderList();
  }

  unmount(): void {
    if (this.container) {
      this.container.innerHTML = '';
      this.container = null;
    }
    this.context = null;
    this.articles = [];
    this.filteredArticles = [];
    this.viewingArticle = null;
  }

  private renderList(): void {
    if (!this.container || !this.context) return;
    const t = this.context.i18n.t.bind(this.context.i18n);
    const isRtl = this.context.i18n.dir === 'rtl';
    const categories = [...new Set(this.articles.map((a) => a.category).filter(Boolean))];

    this.container.innerHTML = `
      <div class="blog-module px-4 pb-24 pt-2 max-w-3xl mx-auto" dir="${isRtl ? 'rtl' : 'ltr'}">
        <h1 class="text-xl font-bold mb-4" style="color: var(--text-primary)">${t('blog_title')}</h1>

        <!-- Search -->
        <div class="mb-4">
          <input type="search" id="blog-search" class="w-full px-4 py-3 rounded-xl text-sm outline-none" style="background: var(--card-bg); color: var(--text-primary); border: 1px solid var(--border)" placeholder="${t('blog_search_placeholder')}" value="${this.searchQuery}" aria-label="${t('blog_search_placeholder')}"/>
        </div>

        <!-- Category Chips -->
        <div class="flex gap-2 overflow-x-auto pb-3 mb-4" role="tablist" style="-webkit-overflow-scrolling: touch; scrollbar-width: none;">
          <button class="blog-cat shrink-0 px-4 py-2 rounded-full text-xs font-medium transition-colors" style="${this.activeCategory === '' ? 'background: var(--accent); color: white;' : 'background: var(--card-bg); color: var(--text-secondary); border: 1px solid var(--border)'}" data-cat="" role="tab" aria-selected="${this.activeCategory === ''}">${t('blog_all_categories')}</button>
          ${categories.map((cat) => `
            <button class="blog-cat shrink-0 px-4 py-2 rounded-full text-xs font-medium transition-colors capitalize" style="${this.activeCategory === cat ? 'background: var(--accent); color: white;' : 'background: var(--card-bg); color: var(--text-secondary); border: 1px solid var(--border)'}" data-cat="${cat}" role="tab" aria-selected="${this.activeCategory === cat}">${cat}</button>
          `).join('')}
        </div>

        <!-- Articles -->
        <div id="blog-list">
          ${this.filteredArticles.length > 0
            ? this.filteredArticles.map((a) => `
              <article class="blog-card rounded-2xl p-4 mb-3 cursor-pointer transition-transform active:scale-[0.98]" style="background: var(--card-bg); box-shadow: var(--shadow-sm)" data-slug="${a.slug}" role="link" tabindex="0" aria-label="${a.title}">
                <div class="flex justify-between items-start gap-3">
                  <div class="flex-1 min-w-0">
                    <h3 class="text-sm font-bold mb-1 line-clamp-2" style="color: var(--text-primary)">${a.title}</h3>
                    <p class="text-xs mb-2 line-clamp-2" style="color: var(--text-secondary)">${a.excerpt || ''}</p>
                    <div class="flex items-center gap-2 flex-wrap">
                      ${a.category ? '<span class="px-2 py-0.5 rounded-full text-xs capitalize" style="background: var(--accent); color: white; opacity: 0.8">' + a.category + '</span>' : ''}
                      <span class="text-xs" style="color: var(--text-secondary)">${a.date || ''}</span>
                      ${a.readTime ? '<span class="text-xs" style="color: var(--text-secondary)">&middot; ' + t('blog_read_time', { minutes: a.readTime }) + '</span>' : ''}
                    </div>
                  </div>
                  ${a.image ? '<img src="' + a.image + '" alt="" class="w-16 h-16 rounded-xl object-cover shrink-0" loading="lazy"/>' : ''}
                </div>
              </article>
            `).join('')
            : '<p class="text-sm text-center py-10" style="color: var(--text-secondary)">' + t('blog_no_results') + '</p>'
          }
        </div>
      </div>
    `;

    // Bind search
    const input = this.container.querySelector<HTMLInputElement>('#blog-search');
    if (input) {
      input.addEventListener('input', () => {
        this.searchQuery = input.value;
        this.applyFilters();
      });
    }

    // Bind category chips
    this.container.querySelectorAll<HTMLButtonElement>('.blog-cat').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.activeCategory = btn.dataset.cat || '';
        this.applyFilters();
      });
    });

    // Bind article cards
    this.container.querySelectorAll<HTMLElement>('.blog-card').forEach((card) => {
      const handler = () => {
        const article = this.articles.find((a) => a.slug === card.dataset.slug);
        if (article) this.showArticle(article);
      };
      card.addEventListener('click', handler);
      card.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handler(); }
      });
    });
  }

  private async showArticle(article: BlogArticle): Promise<void> {
    if (!this.container || !this.context) return;
    const t = this.context.i18n.t.bind(this.context.i18n);
    const isRtl = this.context.i18n.dir === 'rtl';
    this.viewingArticle = article;

    // Loading state
    this.container.innerHTML = `
      <div class="blog-article px-4 pb-24 pt-2 max-w-3xl mx-auto" dir="${isRtl ? 'rtl' : 'ltr'}">
        <button id="blog-back" class="flex items-center gap-2 mb-4 text-sm font-medium" style="color: var(--accent)">
          <svg class="w-4 h-4 ${isRtl ? 'rotate-180' : ''}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
          ${t('blog_back')}
        </button>
        <p class="text-sm text-center py-10" style="color: var(--text-secondary)">${t('blog_loading')}</p>
      </div>
    `;
    this.container.querySelector('#blog-back')?.addEventListener('click', () => {
      this.viewingArticle = null;
      this.renderList();
    });

    // Fetch content
    let content = article.content || '';
    if (!content) {
      for (const ext of ['.md', '.html']) {
        try {
          const resp = await fetch('/blog/' + article.slug + ext, { cache: 'no-store' });
          if (resp.ok) { content = await resp.text(); break; }
        } catch { /* try next */ }
      }
    }

    const rendered = content.startsWith('<') ? content : renderMarkdown(content || article.excerpt || '');

    this.container.innerHTML = `
      <div class="blog-article px-4 pb-24 pt-2 max-w-3xl mx-auto" dir="${isRtl ? 'rtl' : 'ltr'}">
        <button id="blog-back" class="flex items-center gap-2 mb-4 text-sm font-medium" style="color: var(--accent)">
          <svg class="w-4 h-4 ${isRtl ? 'rotate-180' : ''}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
          ${t('blog_back')}
        </button>
        ${article.image ? '<img src="' + article.image + '" alt="" class="w-full h-48 object-cover rounded-2xl mb-4" loading="lazy"/>' : ''}
        <h1 class="text-2xl font-bold mb-2" style="color: var(--text-primary)">${article.title}</h1>
        <div class="flex items-center gap-3 mb-6 flex-wrap">
          ${article.category ? '<span class="px-3 py-1 rounded-full text-xs capitalize" style="background: var(--accent); color: white">' + article.category + '</span>' : ''}
          <span class="text-xs" style="color: var(--text-secondary)">${article.date || ''}</span>
          ${article.readTime ? '<span class="text-xs" style="color: var(--text-secondary)">' + t('blog_read_time', { minutes: article.readTime }) + '</span>' : ''}
        </div>
        <div class="article-content">${rendered}</div>
      </div>
    `;

    this.container.querySelector('#blog-back')?.addEventListener('click', () => {
      this.viewingArticle = null;
      this.renderList();
    });
  }

  private applyFilters(): void {
    const q = this.searchQuery.toLowerCase().trim();
    this.filteredArticles = this.articles.filter((a) => {
      const matchCat = !this.activeCategory || a.category === this.activeCategory;
      const matchSearch = !q || a.title.toLowerCase().includes(q) || (a.excerpt || '').toLowerCase().includes(q) || (a.category || '').toLowerCase().includes(q);
      return matchCat && matchSearch;
    });
    this.renderList();
  }
}
